import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Chip,
  Stack,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import AddIcon from "@mui/icons-material/Add";
import { DeliveryFormRead } from "src/store/api/generatedApi";

interface SavedFormsViewProps {
  savedDeliveries: DeliveryFormRead[];
  loading: boolean;
  error: string | null;
  onEditDelivery: (id: string) => void;
  onDeleteDelivery: (id: string) => void;
  onNewDelivery?: () => void;
}

export const SavedFormsView: React.FC<SavedFormsViewProps> = ({
  savedDeliveries,
  loading,
  error,
  onEditDelivery,
  onDeleteDelivery,
  onNewDelivery,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const handleDeleteClick = (id: string) => {
    setDeleteTargetId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteTargetId) {
      onDeleteDelivery(deleteTargetId);
    }
    setDeleteDialogOpen(false);
    setDeleteTargetId(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteTargetId(null);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(date));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (savedDeliveries.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center", m: 2 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No saved deliveries yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Start recording a delivery to see it appear here.
        </Typography>
      </Paper>
    );
  }

  // For now, treat all deliveries as in progress since we don't have a completion status
  // In the future, we might add a status field to the API model
  const completedDeliveries: DeliveryFormRead[] = [];
  const inProgressDeliveries = savedDeliveries;

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4">Saved Deliveries</Typography>
        {onNewDelivery ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onNewDelivery}
            size="large"
          >
            New Delivery
          </Button>
        ) : null}
      </Stack>

      {/* In Progress Deliveries */}
      {inProgressDeliveries.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <PendingIcon color="warning" />
            All Deliveries ({inProgressDeliveries.length})
          </Typography>
          <Grid container spacing={2}>
            {inProgressDeliveries.map((delivery) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={delivery.id}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      sx={{ mb: 2 }}
                    >
                      <Box sx={{ flex: 1, mr: 1 }}>
                        <Typography
                          variant="h6"
                          component="h3"
                          sx={{ mb: 0.5 }}
                        >
                          {delivery.name ||
                            `${delivery.bay || "Unknown Bay"} - ${
                              delivery.commodity || "Unknown"
                            }`}
                        </Typography>
                        {delivery.name ? (
                          <Typography variant="body2" color="text.secondary">
                            Bay {delivery.bay || "N/A"} •{" "}
                            {delivery.commodity || "No commodity"}
                          </Typography>
                        ) : null}
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => onEditDelivery(delivery.id.toString())}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleDeleteClick(delivery.id.toString())
                          }
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>

                    <Stack spacing={1} sx={{ mb: 2 }}>
                      {!delivery.name && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Commodity:</strong>{" "}
                          {delivery.commodity || "Not specified"}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        <strong>Weight:</strong>{" "}
                        {delivery.mass_lbs && delivery.mass_lbs > 0
                          ? `${delivery.mass_lbs} lbs`
                          : "Not specified"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Truck:</strong>{" "}
                        {delivery.truck_number || "Not specified"}
                      </Typography>
                      {delivery.deviations ? (
                        <Typography variant="body2" color="warning.main">
                          <strong>Deviations:</strong> {delivery.deviations}
                        </Typography>
                      ) : null}
                      {delivery.notes ? (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Notes:</strong>{" "}
                          {delivery.notes.substring(0, 100)}
                          {delivery.notes.length > 100 ? "..." : ""}
                        </Typography>
                      ) : null}
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label="Saved" color="primary" size="small" />
                        {delivery.name ? (
                          <Chip
                            label="Named"
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        ) : null}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(delivery.updated_at)}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Completed Deliveries - Hidden for now since we don't have completion status */}
      {completedDeliveries.length > 0 && (
        <Box>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <CheckCircleIcon color="success" />
            Completed ({completedDeliveries.length})
          </Typography>
          <List>
            {completedDeliveries.map((delivery, index) => (
              <React.Fragment key={delivery.id}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="subtitle1">
                          {delivery.bay || "No Bay Number"}
                        </Typography>
                        <Chip label="Completed" color="success" size="small" />
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          {delivery.commodity || "No commodity"} •{" "}
                          {delivery.mass_lbs && delivery.mass_lbs > 0
                            ? `${delivery.mass_lbs} lbs`
                            : "No weight"}
                        </Typography>
                        {delivery.deviations ? (
                          <Typography variant="body2" color="warning.main">
                            Deviations: {delivery.deviations}
                          </Typography>
                        ) : null}
                        <Typography variant="caption" color="text.secondary">
                          Completed {formatDate(delivery.updated_at)}
                        </Typography>
                      </Stack>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={() => onEditDelivery(delivery.id.toString())}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDeleteClick(delivery.id.toString())
                        }
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < completedDeliveries.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Delivery</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this delivery? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
